import React, {MutableRefObject, useEffect, useRef, useState} from 'react'
import ApplicationSteps, {
  applicationFeeStep,
  applicationFormStep,
  backgroundCheckStep,
  getCurrentStep,
  leaseApplicationSteps
} from './ApplicationSteps'
import ApplicationFeeCard from './ApplicationFeeCard'
import ApplicationFormCard from './ApplicationFormCard'
import BackgroundCheckCard from './BackgroundCheckCard'
import {
  LeaseApplication,
  LeaseApplicationState,
  LeaseApplicationStatus
} from '../../types/app-types'
import {
  useGetLeaseApplicationQuery,
  useGetLeaseUnitDetailQuery,
  useUpdateLeaseApplicationMutation
} from '../../services/residentPortalApi'
import {useParams} from 'react-router-dom'
import Loader from '../common/Loader'
import {getLogger} from '../../services/logging'
import LeaseDetail from './LeaseDetail'
import AppLayout from '../layout/AppLayout'
import {useAppSelector} from '../../store/hooks'
import {selectUser} from '../../store/userSlice'
import toast from 'react-hot-toast'

interface PaymentPollState {
  count: number
  pollRef: any
  pollTimeout: boolean
}

const logger = getLogger('LeaseApplicationPage')

function goBack(
  state: LeaseApplicationState,
  setLeaseApplicationState: Function
): void {
  const {steps, currentStep} = state
  const currentIndex = steps.indexOf(currentStep)
  if (currentIndex > 0) {
    const updatedStep = steps[currentIndex - 1]
    logger.logInfo('Navigating back', {step: updatedStep})
    setLeaseApplicationState({...state, currentStep: updatedStep})
  }
}

function goNext(
  state: LeaseApplicationState,
  setLeaseApplicationState: Function
): void {
  const {steps, currentStep} = state
  const currentIndex = steps.indexOf(currentStep)
  if (currentIndex < steps.length - 1) {
    const updatedStep = steps[currentIndex + 1]
    logger.logInfo('Navigating next', {step: updatedStep})
    setLeaseApplicationState({...state, currentStep: updatedStep})
  }
}

function hasChanges(
  leaseApplication: LeaseApplication,
  updates: Record<string, any>
) {
  for (const key in updates) {
    if (leaseApplication[key as keyof LeaseApplication] !== updates[key]) {
      return true
    }
  }
  return false
}

function doUpdate(
  state: LeaseApplicationState,
  updateLeaseApplication: Function,
  setLeaseApplicationState: Function,
  updates: Record<string, any>
): void {
  const {leaseApplication} = state
  if (hasChanges(leaseApplication, updates)) {
    logger.logInfo('Persisting lease application updates...', {updates})
    const updatedLeaseApplication = Object.assign({}, leaseApplication, updates)
    setLeaseApplicationState({
      ...state,
      leaseApplication: updatedLeaseApplication
    })
    const mutationPromise = updateLeaseApplication(
      updatedLeaseApplication
    ).unwrap()
    toast.promise(mutationPromise, {
      loading: 'Updating lease application...',
      success: 'Lease application updated',
      error: 'Lease application update failed'
    })
  }
}

function pollForPaymentStatus(
  paymentPollStateRef: MutableRefObject<PaymentPollState>,
  updatePaymentPollState: Function,
  refetch: Function
): any {
  return setInterval(() => {
    const paymentPollState = paymentPollStateRef.current
    logger.logInfo(
      `Polling for lease application updates.  Try ${paymentPollState.count}`
    )

    if (paymentPollState.count > 0) {
      // refetch lease application => get payment status
      refetch()

      // decrement poll count
      updatePaymentPollState({
        ...paymentPollState,
        count: paymentPollState.count - 1
      })
    } else {
      // timeout - stop polling
      clearInterval(paymentPollState.pollRef)
      updatePaymentPollState({
        pollRef: null,
        count: 0,
        pollTimeout: true
      })
    }
  }, 15000)
}

const LeaseApplicationPage = () => {
  const [leaseApplicationState, setLeaseApplicationState] = useState<
    LeaseApplicationState
  >({
    steps: leaseApplicationSteps,
    currentStep: null,
    leaseApplication: null
  })
  // A ref is needed because event handlers do not have access to the latest state.
  // See the link for an explanation:
  // https://medium.com/geographit/accessing-react-state-in-event-listeners-with-usestate-and-useref-hooks-8cceee73c559
  const leaseApplicationStateRef = useRef(leaseApplicationState)
  const updateLeaseApplicationState = (updatedState: LeaseApplicationState) => {
    leaseApplicationStateRef.current = updatedState
    setLeaseApplicationState(updatedState)
  }
  const {currentStep, leaseApplication} = leaseApplicationState
  const user = useAppSelector(selectUser)
  const {id} = useParams()
  const getLeaseApplicationQuery = useGetLeaseApplicationQuery(id)
  const [updateLeaseApplication] = useUpdateLeaseApplicationMutation()
  const getLeaseUnitDetailQuery = useGetLeaseUnitDetailQuery(
    {
      propertyId: leaseApplication?.leasePropertyId,
      unitId: leaseApplication?.leaseUnitId
    },
    {skip: !leaseApplication}
  )
  const leaseUnitDetail = getLeaseUnitDetailQuery?.data
  const [paymentPollState, setPaymentPollState] = useState<PaymentPollState>({
    count: 0,
    pollRef: null,
    pollTimeout: false
  })
  const paymentPollStateRef = useRef(paymentPollState)
  const updatePaymentPollState = (updatedState: PaymentPollState) => {
    paymentPollStateRef.current = updatedState
    setPaymentPollState(updatedState)
  }
  const leaseAppStatus =
    leaseApplicationStateRef.current?.leaseApplication?.applicationStatus

  useEffect(() => {
    // init lease application state
    if (!leaseApplication && getLeaseApplicationQuery.isSuccess) {
      const existingLeaseApplication = getLeaseApplicationQuery.data as LeaseApplication
      const initState = {
        steps: leaseApplicationSteps,
        leaseApplication: existingLeaseApplication,
        currentStep: getCurrentStep(existingLeaseApplication.applicationStatus)
      }
      logger.logInfo('Initializing lease application state', {initState})
      updateLeaseApplicationState(initState)
    }

    // handle updates to lease application
    if (leaseApplication && getLeaseApplicationQuery.isSuccess) {
      const updatedLeaseApplication = getLeaseApplicationQuery.data as LeaseApplication
      const updatedState = {
        ...leaseApplicationState,
        leaseApplication: updatedLeaseApplication
      }
      logger.logInfo('Updating lease application state', {updatedState})
      updateLeaseApplicationState(updatedState)
    }
  }, [getLeaseApplicationQuery?.data])

  useEffect(() => {
    // start polling for payment status on an interval
    if (
      leaseAppStatus === LeaseApplicationStatus.PaymentInProgress &&
      !paymentPollState.pollRef &&
      !paymentPollState.pollTimeout
    ) {
      const pollRef = pollForPaymentStatus(
        paymentPollStateRef,
        updatePaymentPollState,
        getLeaseApplicationQuery.refetch
      )
      // timeout: 20 tries * 15s interval => 5m max
      updatePaymentPollState({
        count: 20,
        pollRef,
        pollTimeout: false
      })
    }

    // stop polling when payment is no longer in progress
    if (
      leaseAppStatus !== LeaseApplicationStatus.PaymentInProgress &&
      paymentPollState.pollRef
    ) {
      clearInterval(paymentPollState.pollRef)
      updatePaymentPollState({
        pollRef: null,
        count: 0,
        pollTimeout: false
      })
    }

    // cleanup polling interval
    return () => {
      if (paymentPollState?.pollRef) {
        clearInterval(paymentPollState.pollRef)
      }
    }
  }, [leaseAppStatus])

  if (getLeaseApplicationQuery.isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader text="Loading lease application..." />
      </div>
    )
  } else {
    return (
      <AppLayout>
        <ApplicationSteps applicationState={leaseApplicationState} />

        <LeaseDetail
          className="my-4"
          leaseApplication={leaseApplication}
          leaseUnitDetail={leaseUnitDetail}
        />

        <div className="flex flex-col lg:flex-row gap-2 items-start">
          {currentStep === applicationFormStep ? (
            <ApplicationFormCard
              leaseApplication={leaseApplication}
              leaseUnitDetail={leaseUnitDetail}
              leaseApplicant={user}
              onNext={() =>
                goNext(
                  leaseApplicationStateRef.current,
                  updateLeaseApplicationState
                )
              }
              onUpdate={(updates: Record<string, any>) => {
                logger.logInfo('Updating lease application form...')
                doUpdate(
                  leaseApplicationStateRef.current,
                  updateLeaseApplication,
                  updateLeaseApplicationState,
                  updates
                )
              }}
            />
          ) : (
            ''
          )}

          {currentStep === applicationFeeStep ? (
            <ApplicationFeeCard
              leaseApplication={leaseApplication}
              isPaymentTimeout={paymentPollState.pollTimeout}
              onBack={() =>
                goBack(
                  leaseApplicationStateRef.current,
                  updateLeaseApplicationState
                )
              }
              onNext={() =>
                goNext(
                  leaseApplicationStateRef.current,
                  updateLeaseApplicationState
                )
              }
              onUpdate={(updates: Record<string, any>) => {
                logger.logInfo('Updating lease application fee...')
                doUpdate(
                  leaseApplicationStateRef.current,
                  updateLeaseApplication,
                  updateLeaseApplicationState,
                  updates
                )
              }}
            />
          ) : (
            ''
          )}

          {currentStep === backgroundCheckStep ? (
            <BackgroundCheckCard
              leaseApplication={leaseApplication}
              onBack={() =>
                goBack(
                  leaseApplicationStateRef.current,
                  updateLeaseApplicationState
                )
              }
              onUpdate={(updates: Record<string, any>) => {
                logger.logInfo('Updating lease application background check...')
                doUpdate(
                  leaseApplicationStateRef.current,
                  updateLeaseApplication,
                  updateLeaseApplicationState,
                  updates
                )
              }}
            />
          ) : (
            ''
          )}
        </div>
      </AppLayout>
    )
  }
}

export default LeaseApplicationPage
